import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from '@mui/material';
import { useId } from 'react';
import { useIntl } from 'react-intl';
import type { IncompleteRetrievalState } from './useIncompleteRetrievalDecision';

/** Message ids for what the user would be going on with. */
const ACTION_MESSAGES = {
    open: {
        body: 'patientList.open.incompleteBody',
        cancel: 'patientList.open.incompleteCancel',
        proceed: 'patientList.open.incompleteProceed',
    },
    load: {
        body: 'setup.loadIncompleteBody',
        cancel: 'setup.loadIncompleteCancel',
        proceed: 'setup.loadIncompleteProceed',
    },
} as const;

/**
 * Asks whether to go on with a retrieval that came up short, stating how many
 * files arrived and how many did not, and that the gap may be interpolated.
 *
 * `action` names what going on means — opening the patient, or loading one
 * more dataset into it — so the buttons say what they do.
 */
export default function IncompleteRetrievalDialog({
    state,
    onResolve,
    action,
}: {
    state: IncompleteRetrievalState;
    onResolve: (proceed: boolean) => void;
    action: keyof typeof ACTION_MESSAGES;
}) {
    const intl = useIntl();
    const titleId = useId();
    const bodyId = useId();
    const messages = ACTION_MESSAGES[action];

    return (
        <Dialog
            open={state.open}
            aria-labelledby={titleId}
            aria-describedby={bodyId}
            // No backdrop or Escape dismissal: proceeding with a partial
            // retrieval is a clinical decision. `disableEscapeKeyDown` stops
            // MUI acting on the key but lets it propagate, so a surrounding
            // screen stops listening for Escape while the question is open.
            disableEscapeKeyDown
            PaperProps={{ elevation: 0 }}
        >
            <DialogTitle id={titleId}>
                {intl.formatMessage({
                    id: 'patientList.open.incompleteTitle',
                })}
            </DialogTitle>
            <DialogContent>
                <DialogContentText id={bodyId}>
                    {intl.formatMessage(
                        { id: messages.body },
                        {
                            missing: state.missing,
                            retrieved: state.retrieved,
                        }
                    )}
                </DialogContentText>
                <Alert severity="warning" sx={{ mt: 2 }}>
                    {intl.formatMessage({
                        id: 'patientList.open.incompleteWarning',
                    })}
                </Alert>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={() => onResolve(false)}
                    size="small"
                    color="inherit"
                >
                    {intl.formatMessage({ id: messages.cancel })}
                </Button>
                <Button
                    onClick={() => onResolve(true)}
                    size="small"
                    color="warning"
                    variant="contained"
                >
                    {intl.formatMessage({ id: messages.proceed })}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
